import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import {
  CONTACT_INFO,
  CARD_DESCRIPTOR,
  RETURN_ADDRESS,
  SHIPPING_METHOD,
  SHIPPING_TIME,
  FREE_SHIPPING_THRESHOLD,
  siteImages,
} from '../data/mock';
import { FileText } from 'lucide-react';

const TermsPage = () => {
  useEffect(() => {
    document.title = 'Terms & Conditions | Clarity Lens Co';
  }, []);

  const sections = [
    {
      title: 'Acceptance of Terms',
      content: `By accessing or placing an order on claritylensco.com (the "Site"), operated by ${CONTACT_INFO.corpName} ("Clarity Lens Co," "we," "us," or "our"), you agree to be legally bound by these Terms and Conditions, our Privacy Policy, and our Return & Refund Policy. If you do not agree with any portion of these terms, please discontinue use of the Site and do not submit an order.`,
    },
    {
      title: 'Merchant Information',
      content: `Clarity Lens Co is a trade name operated by ${CONTACT_INFO.corpName}, a United States limited liability company with its principal place of business located at ${CONTACT_INFO.address.street}, ${CONTACT_INFO.address.city}, ${CONTACT_INFO.address.state} ${CONTACT_INFO.address.zip}, ${CONTACT_INFO.address.country}. You may reach customer service at ${CONTACT_INFO.phone} or by email at ${CONTACT_INFO.email}. Customer service hours are Monday through Friday, 9:00 AM to 6:00 PM Pacific Time, excluding U.S. federal holidays.`,
    },
    {
      title: 'Products and Description of Goods',
      content: `Clarity Lens Co sells eyewear and vision-related accessories, including but not limited to blue-light blocking glasses, polarized sunglasses, reading glasses, sports eyewear, night-driving glasses, children's eyewear, eyewear cases and cleaning accessories, and smart audio sunglasses. Product descriptions, images, colors, and specifications are provided for reference only; we make reasonable efforts to display items accurately but do not warrant that colors or appearances on your device exactly match the physical product. All products are intended as non-prescription lifestyle eyewear and are not medical devices. Consult a licensed eye care professional for prescription or medical eyewear needs.`,
    },
    {
      title: 'Pricing and Transaction Currency',
      content: `All prices listed on the Site are in United States Dollars (USD) and do not include applicable taxes, duties, or shipping charges unless expressly stated. The total charge displayed on the checkout confirmation page is the final amount that will be billed to your payment method, excluding any taxes collected where required by law. We reserve the right to correct pricing errors and to modify prices at any time prior to order acceptance.`,
    },
    {
      title: 'Accepted Payment Methods',
      content: `We accept Visa, Mastercard, and American Express credit and debit cards issued by banks located in the United States. Payment is authorized at the time you place your order and captured when your order ships. You represent and warrant that you are the authorized holder of the payment method used and that all information you provide (billing address, card number, expiration date, and security code) is accurate and complete.`,
    },
    {
      title: 'Billing Descriptor',
      content: `Charges will appear on your credit or debit card statement as "${CARD_DESCRIPTOR}". If you do not recognize a charge bearing this descriptor, please contact our customer service team at ${CONTACT_INFO.phone} or ${CONTACT_INFO.email} before initiating a chargeback so we may promptly resolve the issue.`,
    },
    {
      title: 'Order Acceptance and Cancellation',
      content: `Your submission of an order constitutes an offer to purchase. No contract is formed until we accept your order by sending a shipment confirmation email. We reserve the right, at our sole discretion, to refuse or cancel any order for reasons including but not limited to product availability, errors in pricing or product information, or suspected fraudulent activity. If we cancel an order after payment has been captured, we will issue a full refund to the original payment method within 5–7 business days. You may cancel an order free of charge by contacting ${CONTACT_INFO.email} within two (2) hours of placing the order, provided it has not yet been processed for shipment.`,
    },
    {
      title: 'Shipping and Delivery',
      content: `Clarity Lens Co ships exclusively to addresses within the fifty (50) United States. We do not ship to P.O. boxes, APO/FPO addresses, U.S. territories, or international destinations at this time. Orders are shipped via ${SHIPPING_METHOD} with an estimated delivery window of ${SHIPPING_TIME} from the date of shipment. Standard shipping is complimentary on orders of $${FREE_SHIPPING_THRESHOLD} or more; a flat rate applies to orders below that threshold and is displayed at checkout. Delivery timeframes are estimates and are not guaranteed. Risk of loss and title for products pass to you upon delivery to the carrier.`,
    },
    {
      title: 'Returns, Refunds, and Exchanges',
      content: `We offer a 30-day return policy from the date of delivery. Items must be unused, in their original packaging, and in the same condition in which they were received to qualify for a refund. To initiate a return, email ${CONTACT_INFO.email} with your order number. Approved returns must be shipped to ${RETURN_ADDRESS.street}, ${RETURN_ADDRESS.city}, ${RETURN_ADDRESS.state} ${RETURN_ADDRESS.zip}, ${RETURN_ADDRESS.country}. Refunds are issued to the original payment method within 5–7 business days of our receipt and inspection of the returned item. Original shipping charges are non-refundable; return shipping costs are the responsibility of the customer unless the return is due to our error or a defective product.`,
    },
    {
      title: 'Chargebacks and Dispute Resolution',
      content: `If you have any concern about a charge, an order, or the condition of a product you received, we strongly encourage you to contact our customer service team before disputing the charge with your card issuer. Most issues can be resolved quickly through a refund, replacement, or exchange. You agree to work with us in good faith to resolve disputes prior to initiating a chargeback.`,
    },
    {
      title: 'Product Use and Disclaimer',
      content: `Our eyewear products are designed for general lifestyle and protective use only. They are not prescription lenses, medical devices, safety glasses, or personal protective equipment. We make no claims that our products diagnose, treat, cure, or prevent any condition. Consult a qualified eye care professional for specific vision, medical, or occupational safety needs.`,
    },
    {
      title: 'Intellectual Property',
      content: `All content on the Site, including text, photographs, illustrations, logos, graphics, and the "Clarity Lens Co" name and marks, is the property of ${CONTACT_INFO.corpName} or its licensors and is protected by United States and international copyright, trademark, and other intellectual property laws. You may not copy, reproduce, distribute, modify, or create derivative works from any Site content without our prior written consent.`,
    },
    {
      title: 'Consumer Data Privacy',
      content: `We collect, use, and protect your personal information in accordance with our Privacy Policy. We use industry-standard SSL/TLS encryption to protect data transmitted between your browser and our Site, and we partner with PCI-DSS compliant payment processors to handle card data. We do not store full credit card numbers on our servers. Please review our Privacy Policy for complete details on the information we collect and how it is used.`,
    },
    {
      title: 'Export Restrictions and Legal Compliance',
      content: `The Site and our products are intended for use by customers located within the United States. By placing an order, you represent that you are located in the United States and that your purchase complies with all applicable U.S. federal, state, and local laws. We reserve the right to refuse service to any person or in any jurisdiction in our sole discretion.`,
    },
    {
      title: 'Limitation of Liability',
      content: `To the fullest extent permitted by law, ${CONTACT_INFO.corpName} and Clarity Lens Co shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Site or any product purchased from us. Our aggregate liability for any claim shall not exceed the amount you paid for the product giving rise to the claim.`,
    },
    {
      title: 'Governing Law and Jurisdiction',
      content: `These Terms and Conditions are governed by and construed in accordance with the laws of the State of California, without regard to conflict-of-law principles. Any dispute arising out of or relating to these terms or your use of the Site shall be resolved exclusively in the state or federal courts located in Los Angeles County, California, and you consent to the personal jurisdiction of those courts.`,
    },
    {
      title: 'Changes to These Terms',
      content: `We reserve the right to revise these Terms and Conditions at any time. Changes are effective as of the "Last updated" date shown below and will be posted on this page. Your continued use of the Site after changes are posted constitutes your acceptance of the revised terms.`,
    },
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <CartDrawer />

      <main className="pt-[105px]">
        {/* Hero Banner */}
        <section className="relative h-[200px] overflow-hidden">
          <img
            src={siteImages.privacyBanner}
            alt="Terms and Conditions"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/90 via-neutral-900/70 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-xl">
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-xs font-medium mb-4">
                  <FileText className="w-3.5 h-3.5" />
                  Legal Information
                </span>
                <h1 className="font-display text-3xl sm:text-4xl text-white font-semibold">
                  Terms & Conditions
                </h1>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white border border-neutral-200 rounded-2xl p-8 md:p-12">
            <p className="text-neutral-600 mb-8">
              Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>

            <p className="text-neutral-700 mb-8 leading-relaxed">
              Welcome to Clarity Lens Co. Please read these Terms and Conditions carefully before using our website or making a purchase. These terms, together with our Privacy Policy and Return & Refund Policy, govern every transaction between you and {CONTACT_INFO.corpName}.
            </p>

            <div className="space-y-8">
              {sections.map((section, i) => (
                <div key={i}>
                  <h2 className="font-semibold text-neutral-900 text-lg mb-3">
                    {i + 1}. {section.title}
                  </h2>
                  <p className="text-neutral-600 leading-relaxed whitespace-pre-line">{section.content}</p>
                </div>
              ))}
            </div>

            {/* Contact */}
            <div className="mt-12 pt-8 border-t border-neutral-200">
              <h2 className="font-semibold text-neutral-900 text-lg mb-4">Contact Information</h2>
              <p className="text-neutral-600 mb-4">
                For questions about these Terms and Conditions, billing inquiries, or to dispute a charge, please contact us before initiating a chargeback with your card issuer:
              </p>
              <div className="bg-neutral-50 rounded-xl p-6">
                <p className="text-neutral-900 font-medium">{CONTACT_INFO.corpName}</p>
                <p className="text-neutral-700 text-sm">(doing business as Clarity Lens Co)</p>
                <p className="text-neutral-600 mt-2">
                  {CONTACT_INFO.address.street}<br />
                  {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}<br />
                  {CONTACT_INFO.address.country}
                </p>
                <p className="text-neutral-600 mt-2">
                  Email:{' '}
                  <a href={`mailto:${CONTACT_INFO.email}`} className="text-copper-600 hover:underline">
                    {CONTACT_INFO.email}
                  </a>
                </p>
                <p className="text-neutral-600">
                  Phone:{' '}
                  <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="text-copper-600 hover:underline">
                    {CONTACT_INFO.phone}
                  </a>
                </p>
                <p className="text-neutral-600 mt-3 text-sm">
                  Card statements will show charges as <span className="font-semibold text-neutral-900">"{CARD_DESCRIPTOR}"</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsPage;
